import PropTypes from 'prop-types';
import { Typography, Space, Breadcrumb } from 'antd';
import { Link } from 'react-router-dom';
import IconTile from './IconTile';
import './primitives.css';

const { Title, Text } = Typography;

/**
 * PageHeader
 *
 * One 40px band at the top of a page: title left, actions right, on the same
 * baseline (`docs/design-language.md` §3 rule 4). The subtitle rides the
 * title's baseline rather than stacking under it — a page header that costs two
 * lines before any content is the cheapest density there is to give back — and
 * drops to its own line only when the row wraps.
 *
 *   - Title + optional subtitle
 *   - Optional icon, which is an IconTile, not a bare glyph
 *   - Optional breadcrumb trail
 *   - Optional right-aligned action slot (filters, primary CTAs)
 */
const PageHeader = ({
  title,
  subtitle,
  icon,
  tone = 'primary',
  breadcrumbs,
  actions,
  bordered = true,
  compact = false,
  className = '',
}) => (
  <header
    className={[
      'lms-page-header',
      bordered ? 'lms-page-header--bordered' : '',
      compact ? 'lms-page-header--compact' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {breadcrumbs?.length > 0 && (
      <Breadcrumb
        className="lms-page-header__breadcrumb"
        items={breadcrumbs.map((b) => ({
          title: b.path ? <Link to={b.path}>{b.label}</Link> : b.label,
        }))}
      />
    )}

    <div className="lms-page-header__row">
      <div className="lms-page-header__title-block">
        {icon && <IconTile icon={icon} tone={tone} size="sm" className="lms-page-header__icon" />}
        <div className="lms-page-header__title-stack">
          <Title level={3} className="lms-page-header__title">
            {title}
          </Title>
          {subtitle && (
            <Text type="secondary" className="lms-page-header__subtitle">
              {subtitle}
            </Text>
          )}
        </div>
      </div>

      {actions && (
        <div className="lms-page-header__actions">
          <Space size="small" wrap>
            {actions}
          </Space>
        </div>
      )}
    </div>
  </header>
);

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  /** Tile colour for `icon`. Defaults to the tenant accent, the one slot it owns. */
  tone: IconTile.propTypes.tone,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      path: PropTypes.string,
    }),
  ),
  actions: PropTypes.node,
  bordered: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string,
};

export default PageHeader;
